/**
 * Resolves the Optro instance base URL from the "Sign in with Optro" dialog.
 *
 * The dialog collects a subdomain (the customer/instance) plus a domain suffix
 * (the environment). On localhost it collects a `localhost[:port]` host instead.
 *
 * Which suffixes are offered is deployment configuration, not something the
 * frontend infers: `VITE_OPTRO_DOMAIN_SUFFIXES` carries the selectable list, set
 * per environment in ArgoCD, or the literal `all` for every known environment.
 * It replaced a hardcoded production-hostname set, which only knew about the
 * single-region `midship.ai` deploy and so silently fell through to the full
 * environment list on every multi-tenant region. Knowing which environment we
 * are in is the deployment's job, not something to re-derive from the URL.
 *
 * The suffix list is kept in sync with the backend allowlist
 * (`_ALLOWED_OPTRO_BASE_DOMAINS` in optro_oauth.py) — the backend is the
 * security boundary; this list only drives the UI.
 *
 * Order is not cosmetic: the dialog pre-selects the first suffix it is offered,
 * so both lists below lead with `auditboardapp.com`. Only that canonical
 * AuditBoard host completes sign-in today — Midship builds the
 * `private_key_jwt` assertion's `aud` from the host the user arrived on, while
 * auditboard-backend pins the expected value to the instance's configured
 * canonical host (`v1.soxhub.url`) and accepts that single value only, so
 * entering through `<instance>.optroapp.ai` completes SSO and is then refused
 * at the callback with `invalid_client`. `optroapp.ai` stays selectable; this
 * only decides which host a user gets without choosing.
 */
import { config } from "@/config";

/**
 * Offered when `VITE_OPTRO_DOMAIN_SUFFIXES` is unset.
 *
 * Deliberately the two production domains rather than the full list: an
 * unconfigured deployment should fail towards showing too few environments, not
 * towards offering a customer a staging domain.
 */
export const DEFAULT_OPTRO_DOMAIN_SUFFIXES = [
  "auditboardapp.com",
  "optroapp.ai",
] as const;

/**
 * Every known environment suffix. Offered where the deployment asks for them by
 * setting `VITE_OPTRO_DOMAIN_SUFFIXES` to `all` or to an explicit list, and on
 * the known non-production hosts in `NON_PRODUCTION_FRONTEND_HOSTS`. No URL can
 * widen the dropdown to these, and production is neither configured for them nor
 * on that host list, so production offers the production pair and nothing else.
 * `auditboardapp.com` leads because the dialog pre-selects the first entry (see
 * the module comment); the remaining Optro (`.ai`) environments come next, then
 * the other legacy AuditBoard (`.com`) equivalents.
 */
export const OPTRO_DOMAIN_SUFFIXES = [
  // AuditBoard production — first so it is the pre-selected default.
  "auditboardapp.com",
  // Optro
  "optroapp.ai",
  "optrostaging.ai",
  "qe.optrostaging.ai",
  "pr.optrostaging.ai",
  "load.optrostaging.ai",
  "fedramp.optrostaging.ai",
  "optrodebug.ai",
  "optrodemo.ai",
  "product.optrodemo.ai",
  "se.optrodemo.ai",
  "optrosandbox.ai",
  "optrosupport.ai",
  "partners.optrosupport.ai",
  "optrotest.ai",
  "optrotrial.ai",
  // AuditBoard (legacy)
  "auditboarddebug.com",
  "auditboarddemo.com",
  "product.auditboarddemo.com",
  "se.auditboarddemo.com",
  "auditboardsandbox.com",
  "auditboardsupport.com",
  "partners.auditboardsupport.com",
  "auditboardtrial.com",
  "soxhubapp.com",
  "qa.soxhubapp.com",
  "pr.soxhubapp.com",
  "load.soxhubapp.com",
  "qafedramp.soxhubapp.com",
  "soxdev.com",
] as const;

export function isLocalhostFrontend(): boolean {
  return window.location.hostname === "localhost";
}

/**
 * Frontend hosts known to be non-production deployments.
 *
 * An allowlist of NON-production hosts, and deliberately the inverse of the
 * hardcoded production-hostname set this module used to carry. That one had to
 * recognise every production host to stay correct, and silently fell through to
 * the full environment list on each multi-tenant region it did not know — it
 * failed open. This fails the other way: a host absent from the set gets
 * deployment config, which is the production pair unless a deployment says
 * otherwise. Adding a production region can therefore never widen its dropdown
 * by omission.
 *
 * Exact hostnames only, never a suffix or pattern, so nothing can match into it
 * accidentally.
 *
 * This exists because the dev deployment cannot be configured the normal way:
 * its container entrypoint does not run, so `window.__ENV__` is empty, and the
 * Dockerfile passes no `VITE_*` build args either, leaving no runtime or
 * build-time channel for `VITE_OPTRO_DOMAIN_SUFFIXES`. Delete this once dev can
 * receive deployment config like every other environment. QA is deliberately
 * absent: its entrypoint works, so it is configured, not special-cased.
 */
const NON_PRODUCTION_FRONTEND_HOSTS: ReadonlySet<string> = new Set([
  "dev.midship.dev",
]);

export function isNonProductionFrontend(): boolean {
  return NON_PRODUCTION_FRONTEND_HOSTS.has(window.location.hostname);
}

/**
 * Whether `?signInWithOptro=1` is on the current URL. One caller reads it:
 * `SignIn` renders the "Sign in with Optro" button when it is set or when the
 * LaunchDarkly flag is on. It does NOT affect which domains the dialog offers —
 * that is deployment config alone, see `selectableOptroSuffixes`.
 *
 * Read per call rather than captured, so it never outlives the URL that set it.
 * This only decides whether the button is visible. It grants nothing: reaching
 * any Optro instance still requires credentials there, and
 * `_ALLOWED_OPTRO_BASE_DOMAINS` in the backend's `optro_oauth.py` bounds which
 * hosts a sign-in can be sent to at all.
 */
export function isSignInWithOptroOverride(): boolean {
  return (
    new URLSearchParams(window.location.search).get("signInWithOptro") === "1"
  );
}

/**
 * Parse a comma-separated suffix list into normalized, deduplicated entries.
 *
 * Unknown suffixes are dropped rather than trusted: the value is deployment
 * config, and a typo that reached the dropdown would build a base URL the
 * backend allowlist rejects only after the user has committed to a redirect.
 */
export function parseSuffixList(raw: string | undefined): string[] {
  if (!raw) return [];
  const known = new Set<string>(OPTRO_DOMAIN_SUFFIXES);
  const seen = new Set<string>();
  const parsed: string[] = [];
  for (const entry of raw.split(",")) {
    const suffix = entry.trim().toLowerCase();
    if (!suffix || seen.has(suffix) || !known.has(suffix)) continue;
    seen.add(suffix);
    parsed.push(suffix);
  }
  return parsed;
}

/**
 * Value of `VITE_OPTRO_DOMAIN_SUFFIXES` that offers every known environment.
 *
 * A sentinel rather than a spelled-out list so an environment that wants
 * everything cannot drift from `OPTRO_DOMAIN_SUFFIXES` when a suffix is added
 * here and nobody remembers to update the deployment config too. Checked before
 * `parseSuffixList`, which would otherwise discard it as an unknown suffix.
 */
export const ALL_OPTRO_DOMAIN_SUFFIXES = "all";

/**
 * The suffixes the dialog should offer, most-preferred first. The first entry is
 * what the dropdown pre-selects.
 */
export function selectableOptroSuffixes(): string[] {
  if (isNonProductionFrontend()) return [...OPTRO_DOMAIN_SUFFIXES];
  if (
    config.optroDomainSuffixes.trim().toLowerCase() ===
    ALL_OPTRO_DOMAIN_SUFFIXES
  ) {
    return [...OPTRO_DOMAIN_SUFFIXES];
  }
  const configured = parseSuffixList(config.optroDomainSuffixes);
  return configured.length > 0
    ? configured
    : [...DEFAULT_OPTRO_DOMAIN_SUFFIXES];
}

function cleanSubdomain(input: string): string {
  return input
    .trim()
    .replace(/^https?:\/\//i, "")
    .replace(/\/+$/, "");
}

const LABELS =
  /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*$/i;

/**
 * Read typed input as a full Optro host, e.g. `acme.optroapp.ai`,
 * `wes-midship-hybrid-testing.optrostaging.ai`, or a URL pasted from the
 * address bar (`https://acme.auditboardapp.com/dashboard`).
 *
 * - `undefined`: not a full host — a single label like `acme`, which the
 *   dialog combines with the selected environment.
 * - `null`: a dotted host that is not under a known Optro/AuditBoard domain,
 *   or is only the bare domain with no instance label. Rejected rather than
 *   having the dropdown suffix appended, which would build a host like
 *   `acme.optrostaging.ai.auditboardapp.com` that passes the backend allowlist
 *   as a subdomain of `auditboardapp.com` and then does not exist.
 * - otherwise the normalized hostname.
 *
 * Matched against every known domain (`isKnownOptroHostname`), not just the
 * environments this deployment offers in the dropdown: a user who types the
 * full address of their instance can reach any instance the backend's
 * `_ALLOWED_OPTRO_BASE_DOMAINS` accepts, including a non-production one from a
 * production deployment. The dropdown itself stays deployment-scoped.
 */
export function fullOptroHost(input: string): string | null | undefined {
  const trimmed = input.trim();
  if (!trimmed) return undefined;
  const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;
  let url: URL;
  try {
    url = new URL(withScheme);
  } catch {
    return trimmed.includes(".") ? null : undefined;
  }
  const host = url.hostname.toLowerCase();
  if (!host.includes(".")) return undefined;
  if (url.port || !LABELS.test(host)) return null;
  const isBareDomain = OPTRO_DOMAIN_SUFFIXES.some((suffix) => host === suffix);
  if (isBareDomain || !isKnownOptroHostname(host)) return null;
  return host;
}

/**
 * Build the instance base URL from a subdomain + environment suffix.
 *
 * - `localhost` / `localhost:PORT` → `https://localhost:PORT` (default 9002); the
 *   suffix is ignored. localhost is a host, not a subdomain.
 * - a full Optro host (see `fullOptroHost`) → `https://<host>`; the suffix is
 *   ignored.
 * - otherwise → `https://<subdomain>.<suffix>`, where `suffix` must appear in
 *   `allowedSuffixes` (passed in, so the check matches whatever the dialog is
 *   actually offering rather than a wider list).
 *
 * Returns `null` for empty/invalid input so the caller can block submission.
 */
export function buildOptroBaseUrl(
  subdomain: string,
  suffix: string,
  allowedSuffixes: readonly string[],
): string | null {
  const sub = cleanSubdomain(subdomain);
  if (!sub) return null;

  if (sub === "localhost" || /^localhost:\d+$/.test(sub)) {
    const port = sub.includes(":") ? sub.split(":")[1] : "9002";
    return `https://localhost:${port}`;
  }

  const fullHost = fullOptroHost(subdomain);
  if (fullHost !== undefined) {
    return fullHost === null ? null : `https://${fullHost}`;
  }

  if (!LABELS.test(sub)) return null;
  if (!allowedSuffixes.some((s) => s === suffix)) return null;
  return `https://${sub}.${suffix}`;
}

/** Prefill for the subdomain field: on localhost, default to `localhost:9002`. */
export function defaultOptroSubdomain(): string {
  return isLocalhostFrontend() ? "localhost:9002" : "";
}

/**
 * Whether `hostname` is (or is a subdomain of) a known Optro/AuditBoard
 * domain — checked against the full `OPTRO_DOMAIN_SUFFIXES`, not this
 * deployment's narrower `selectableOptroSuffixes()`. It decides whether to
 * attempt a referrer-based auto sign-in (`optroBaseUrlFromReferrer`) and
 * whether a typed full host is accepted (`fullOptroHost`); the backend's own
 * `_ALLOWED_OPTRO_BASE_DOMAINS`
 * allowlist in `optro_oauth.py` remains the actual security gate on which
 * host a kickoff can be sent to.
 */
export function isKnownOptroHostname(hostname: string): boolean {
  const host = hostname.toLowerCase();
  return OPTRO_DOMAIN_SUFFIXES.some(
    (suffix) => host === suffix || host.endsWith(`.${suffix}`),
  );
}

/**
 * The Optro instance base URL implied by `document.referrer`, when the
 * browser arrived here from a known Optro/AuditBoard domain (e.g. a link from
 * the Optro dashboard). `null` for an empty, same-origin, unparsable, or
 * unrecognized referrer.
 *
 * A `localhost` referrer counts only when this frontend is itself on
 * localhost — the same rule `buildOptroBaseUrl` applies to typed input — so a
 * local Optro on another port can drive the flow end to end in local dev.
 */
export function optroBaseUrlFromReferrer(referrer: string): string | null {
  if (!referrer) return null;
  let url: URL;
  try {
    url = new URL(referrer);
  } catch {
    return null;
  }
  if (url.origin === window.location.origin) return null;
  if (url.hostname === "localhost") {
    return isLocalhostFrontend() ? url.origin : null;
  }
  if (url.protocol !== "https:") return null;
  return isKnownOptroHostname(url.hostname) ? url.origin : null;
}
